import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Card, Divider, Typography, useTheme } from '@mui/material';
import { Accept, ErrorCode, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { AlertMessage } from '@eposnow/ui-core';
import { ReactComponent as UploadFileIcon } from '../../../../img/icons/uploadFile.svg';
import { ReactComponent as WhiteUploadFileIcon } from '../../../../img/icons/uploadFile-white.svg';
import { UIContext } from '../../../../context/UIContext';
import { UserContext } from '../../../../context/UserContext';
import { ACCEPT_PNG_JPG_JPEG, ACCEPT_XLSX_CSV, MAX_FILE_SIZE } from '../../../../constants';
import { isFileImage } from '../../../../helpers/helpers';

export interface FileUploaderProps {
  file: File;
  setFile: React.Dispatch<React.SetStateAction<File>>;
  imageOnly?: boolean;
  hideAlert?: boolean;
  mainAction?: boolean;
}

const FileUploader = ({
  file,
  setFile,
  imageOnly = false,
  hideAlert = false,
  mainAction = false,
}: FileUploaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { colorMode } = useContext(UIContext);
  const { locale } = useContext(UserContext);
  const [filePreview, setFilePreview] = useState(null);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const { getRootProps, getInputProps } = useDropzone({
    accept: { ...ACCEPT_PNG_JPG_JPEG, ...(!imageOnly && { ...ACCEPT_XLSX_CSV }) },
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    maxFiles: 1,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileErrors && fileErrors.length) setFileErrors([]);
      if (fileRejections && fileRejections.length) {
        const logoErrors = fileRejections[0].errors;
        logoErrors.forEach((error) => {
          if (error.code === ErrorCode.FileInvalidType) {
            setFileErrors((prevErrors) => [
              ...prevErrors,
              t('screens.products.sidekick.import.errorFileType', {
                fileFormat: imageOnly ? '.JPG, .JPEG or .PNG' : '.JPG, .JPEG, .PNG, .XLSX or .CSV',
              }),
            ]);
          } else if (error.code === ErrorCode.FileTooLarge) {
            setFileErrors((prevErrors) => [
              ...prevErrors,
              t('screens.products.sidekick.import.errorFileLarge', { size: '2.5MB' }),
            ]);
          }
        });
      } else {
        setFile(acceptedFiles[0]);
        if (isFileImage(acceptedFiles[0]))
          setFilePreview(
            Object.assign(acceptedFiles[0], {
              preview: URL.createObjectURL(acceptedFiles[0]),
            })
          );
        else setFilePreview(null);
      }
    },
  });

  const handleRemoveFile = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    if (fileErrors && fileErrors.length) setFileErrors([]);
    setFile(null);
  };

  return (
    <Card
      variant="outlined"
      component="div"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        border: '2px dashed',
        borderColor: (currTheme) =>
          colorMode === 'light'
            ? currTheme?.palette?.grey?.['12p']
            : currTheme?.palette?.grey?.['23p'],
        marginY: 2,
        padding: 3,
        gap: '0.5rem',
        cursor: 'grab',
      }}
      role={undefined}
      tabIndex={-1}
      {...getRootProps()}
    >
      {!file ? (
        <>
          {colorMode === 'light' ? <UploadFileIcon /> : <WhiteUploadFileIcon />}
          <Typography variant="h3">{t('screens.products.sidekick.import.fileImportDialog.dragAndDrop')}</Typography>
          <Box display="flex" alignItems="center">
            <Divider sx={{ width: '48px', marginTop: '3px' }} />
            <Typography sx={{ marginX: 1 }} variant="body2">
              {t('screens.products.sidekick.import.orOption')}
            </Typography>
            <Divider sx={{ width: '48px', marginTop: '3px' }} />
          </Box>
          <Button
            id="browseLogo"
            aria-label={t('screens.products.sidekick.import.fileImportDialog.browseFile')}
            variant="contained"
            data-qa-id="browse-file-button"
            disableElevation
            // {...getRootProps()}
          >
            {t('screens.products.sidekick.import.fileImportDialog.browseFile')}
            <input data-testid="uploadInput" {...getInputProps()} />
          </Button>
          {fileErrors &&
            fileErrors.length > 0 &&
            fileErrors.map((error) => (
              <Box key={error} sx={{ marginTop: 2, width: '100%' }}>
                <AlertMessage type="error" locale={locale} theme={theme}>
                  <Typography component="span" variant="body2" gutterBottom>
                    {error}
                  </Typography>
                </AlertMessage>
              </Box>
            ))}
        </>
      ) : (
        <>
          {filePreview && (
            <Box sx={{ height: '100px' }}>
              <img
                src={filePreview.preview}
                style={{
                  display: 'block',
                  width: 'auto',
                  height: '100%',
                }}
                alt={filePreview.name}
                // Revoke data uri after image is loaded
                onLoad={() => {
                  URL.revokeObjectURL(filePreview.preview);
                }}
              />
            </Box>
          )}
          {!filePreview && (colorMode === 'light' ? <UploadFileIcon /> : <WhiteUploadFileIcon />)}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {t('screens.products.sidekick.import.fileImportDialog.fileName')}
            </Typography>
            <Typography data-qa-id="file-name" variant="body2">
              {file.name}
            </Typography>
          </Box>
          <Button
            onClick={handleRemoveFile}
            variant="outlined"
            color="error"
            data-qa-id="remove-file-button"
            sx={{ backgroundColor: theme?.palette?.background?.paper, mt: 1 }}
          >
            {t('screens.products.sidekick.import.fileImportDialog.removeFile')}
          </Button>
        </>
      )}
    </Card>
  );
};

export default FileUploader;

